// Telegram session card for the profile detail pane. Mirrors the desktop's
// `AuthForm` visually (tg-surface card, same typographic scale, same input
// + button classes) but drives the server-side state machine via HTTP +
// consumes live state from the WebSocket gateway.
import type { InputHTMLAttributes } from "react";
import { useCallback, useEffect, useState } from "react";
import type {
	TelegramAuthInputStep,
	TelegramAuthStep,
	TelegramSessionState,
} from "@shared/api/schemas/telegram";
import { ApiError, httpClient } from "../../client/http-client";
import { wsClient } from "../../client/ws-client";
import { useLiveResource } from "../../hooks/useLiveResource";

const inputClassName =
	"tg-input tg-text-primary h-12 w-full rounded-[16px] px-4";
const primaryButtonClassName =
	"tg-btn-primary inline-flex h-10 items-center justify-center rounded-xl px-4 text-[0.95rem] font-semibold disabled:cursor-wait disabled:opacity-60";
const secondaryButtonClassName =
	"tg-btn-secondary inline-flex h-10 items-center gap-2 rounded-xl px-3 text-[0.9rem] font-semibold disabled:cursor-not-allowed disabled:opacity-55";

const FIELD_COPY: Record<
	TelegramAuthInputStep,
	{
		buttonLabel: string;
		inputMode?: InputHTMLAttributes<HTMLInputElement>["inputMode"];
		label: string;
		placeholder: string;
		type: string;
	}
> = {
	"phone-number": {
		label: "Phone number",
		placeholder: "+49 151 23456789",
		buttonLabel: "Continue",
		inputMode: "tel",
		type: "tel",
	},
	"email-address": {
		label: "Email address",
		placeholder: "name@example.com",
		buttonLabel: "Continue",
		inputMode: "email",
		type: "email",
	},
	"email-code": {
		label: "Email code",
		placeholder: "Enter the email code",
		buttonLabel: "Verify",
		inputMode: "numeric",
		type: "text",
	},
	"auth-code": {
		label: "Telegram code",
		placeholder: "Enter the login code",
		buttonLabel: "Verify",
		inputMode: "numeric",
		type: "text",
	},
	password: {
		label: "2-step verification password",
		placeholder: "Enter your password",
		buttonLabel: "Unlock",
		type: "password",
	},
};

const isInputStep = (step: TelegramAuthStep): step is TelegramAuthInputStep =>
	step in FIELD_COPY;

interface TelegramLoginCardProps {
	profileId: string;
}

export const TelegramLoginCard = ({ profileId }: TelegramLoginCardProps) => {
	const [error, setError] = useState<string | null>(null);
	const [notInteractive, setNotInteractive] = useState(false);
	const [singleValue, setSingleValue] = useState("");
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [restarting, setRestarting] = useState(false);

	// Telegram session state via useLiveResource (initial fetch + WS
	// updates + re-fetch on backend running + re-fetch on WS reconnect).
	const {
		value: state,
		error: fetchError,
		setValue: setState,
	} = useLiveResource<TelegramSessionState>({
		key: profileId,
		fetcher: () => httpClient.telegram.getSession(profileId),
		subscribe: (onUpdate) =>
			wsClient.onTelegramChanged((incomingProfileId, next) => {
				if (incomingProfileId === profileId) onUpdate(next);
			}),
		refetchOnBackendRunning: { profileId },
	});

	// Map fetch errors into the existing component state shape.
	useEffect(() => {
		setError(null);
		setNotInteractive(false);
		if (!fetchError) return;
		if (fetchError instanceof ApiError && fetchError.code === "NOT_INTERACTIVE") {
			setNotInteractive(true);
		} else if (fetchError instanceof ApiError && fetchError.status === 502) {
			// Backend isn't running — show "Start the backend…" copy.
		} else {
			setError(
				fetchError instanceof ApiError
					? fetchError.message
					: String(fetchError),
			);
		}
	}, [fetchError]);

	// When the backend transitions OUT of `running`, blank the local
	// Telegram state in-place. We deliberately do NOT call refetch() here
	// because the /telegram/session route goes through BackendProxyController
	// with `lazyStart: true` — a fetch on a stopped backend would
	// resurrect it as a side effect, which is the opposite of what the
	// user wanted when they hit Stop.
	//
	// useLiveResource's refetchOnBackendRunning handles the
	// other direction: backend → running re-fetches automatically.
	useEffect(() => {
		const unsubscribe = wsClient.onBackendChanged((incoming, backend) => {
			if (incoming !== profileId) return;
			if (backend.status !== "running") setState(null);
		});
		return unsubscribe;
	}, [profileId, setState]);

	// Reset local form state when the step changes so stale inputs don't leak.
	useEffect(() => {
		setSingleValue("");
		setFirstName("");
		setLastName("");
		setError(null);
		setRestarting(false);
	}, [state?.step]);

	const handleSingleSubmit = useCallback(
		async (step: TelegramAuthInputStep) => {
			setError(null);
			try {
				const next = await httpClient.telegram.submitAuth(
					profileId,
					step,
					singleValue,
				);
				setState(next);
			} catch (err) {
				setError(err instanceof ApiError ? err.message : String(err));
			}
		},
		[profileId, singleValue],
	);

	const handleRegistrationSubmit = useCallback(async () => {
		setError(null);
		try {
			const next = await httpClient.telegram.submitRegistration(profileId, {
				firstName,
				lastName,
			});
			setState(next);
		} catch (err) {
			setError(err instanceof ApiError ? err.message : String(err));
		}
	}, [profileId, firstName, lastName]);

	const handleLogout = useCallback(async () => {
		setError(null);
		setRestarting(true);
		try {
			const next = await httpClient.telegram.logout(profileId);
			setState(next);
		} catch (err) {
			setError(err instanceof ApiError ? err.message : String(err));
		} finally {
			setRestarting(false);
		}
	}, [profileId]);

	if (notInteractive) {
		return (
			<section className="tg-surface rounded-2xl p-4">
				<h3 className="tg-text-primary text-[0.95rem] font-semibold leading-5">
					Telegram
				</h3>
				<p className="tg-text-muted mt-2 text-[0.85rem] leading-6">
					This backend wasn’t launched in interactive mode. The orchestrator
					is responsible for configuring children that way — restart the
					backend from the runtime card above.
				</p>
			</section>
		);
	}

	if (!state) {
		return (
			<section className="tg-surface rounded-2xl p-4">
				<h3 className="tg-text-primary text-[0.95rem] font-semibold leading-5">
					Telegram
				</h3>
				<p className="tg-text-muted mt-2 text-[0.85rem] leading-6">
					Start the backend for this profile to begin the Telegram login
					flow.
				</p>
				{error ? (
					<div
						className="tg-alert-error mt-3 rounded-lg px-3 py-2 text-[0.85rem]"
						role="alert"
					>
						{error}
					</div>
				) : null}
			</section>
		);
	}

	const renderBody = () => {
		if (state.step === "ready") {
			return (
				<div className="tg-surface-soft mt-5 rounded-2xl p-4">
					<h4 className="tg-text-primary text-[0.95rem] font-semibold">
						Connected to Telegram
					</h4>
					<p className="tg-text-muted mt-1.5 text-[0.85rem] leading-6">
						{state.account ? (
							<>
								Signed in as{" "}
								<span className="tg-text-primary font-semibold">
									{state.account.displayName}
								</span>
								{state.account.phoneNumber
									? ` · ${state.account.phoneNumber}`
									: ""}
							</>
						) : (
							"Signed in."
						)}
					</p>
					<p className="tg-text-muted mt-1.5 text-[0.85rem] leading-6">
						{state.channels.length} chat{state.channels.length === 1 ? "" : "s"}{" "}
						loaded.
					</p>
					<div className="mt-4 flex items-center gap-2">
						<button
							type="button"
							onClick={() => void handleLogout()}
							disabled={restarting}
							className={secondaryButtonClassName}
						>
							{restarting ? "Logging out..." : "Log out of Telegram"}
						</button>
					</div>
				</div>
			);
		}

		if (state.step === "booting") {
			return (
				<div className="tg-surface-soft mt-5 flex items-center gap-4 rounded-2xl p-4">
					<div
						className="tg-spinner h-[28px] w-[28px] animate-spin rounded-full border-[3px]"
						aria-hidden="true"
					/>
					<p className="tg-text-secondary leading-6">{state.description}</p>
				</div>
			);
		}

		if (state.step === "other-device") {
			return (
				<div className="tg-surface-soft mt-5 rounded-2xl p-4">
					<p className="tg-text-secondary leading-7">
						Approve the login request on one of your existing Telegram
						devices to continue here.
					</p>
					{state.otherDeviceLink ? (
						<code className="tg-code-block mt-3 block break-all rounded-2xl px-[14px] py-3">
							{state.otherDeviceLink}
						</code>
					) : null}
				</div>
			);
		}

		if (state.step === "registration") {
			return (
				<form
					className="mt-5 grid gap-3.5"
					onSubmit={(event) => {
						event.preventDefault();
						void handleRegistrationSubmit();
					}}
				>
					<label className="grid gap-2">
						<span className="tg-text-primary text-[0.92rem] font-semibold">
							First name
						</span>
						<input
							className={inputClassName}
							autoFocus
							value={firstName}
							onChange={(event) => setFirstName(event.target.value)}
							placeholder="First name"
							type="text"
							disabled={state.isBusy}
						/>
					</label>
					<label className="grid gap-2">
						<span className="tg-text-primary text-[0.92rem] font-semibold">
							Last name
						</span>
						<input
							className={inputClassName}
							value={lastName}
							onChange={(event) => setLastName(event.target.value)}
							placeholder="Last name (optional)"
							type="text"
							disabled={state.isBusy}
						/>
					</label>
					<button
						className={primaryButtonClassName}
						type="submit"
						disabled={state.isBusy}
					>
						{state.isBusy ? "Saving..." : "Continue"}
					</button>
				</form>
			);
		}

		if (state.step === "error") {
			return (
				<div className="tg-alert-error mt-5 rounded-2xl p-4">
					<p className="leading-7">
						{state.error ??
							"Something went wrong with the Telegram login flow. Restart the backend or try logging out."}
					</p>
					<div className="mt-3 flex items-center gap-2">
						<button
							type="button"
							onClick={() => void handleLogout()}
							disabled={restarting}
							className={secondaryButtonClassName}
						>
							{restarting ? "Resetting..." : "Reset Telegram session"}
						</button>
					</div>
				</div>
			);
		}

		if (!isInputStep(state.step)) return null;
		const inputStep = state.step;
		const field = FIELD_COPY[inputStep];

		return (
			<form
				className="mt-5 grid gap-3.5"
				onSubmit={(event) => {
					event.preventDefault();
					void handleSingleSubmit(inputStep);
				}}
			>
				<label className="grid gap-2">
					<span className="tg-text-primary text-[0.92rem] font-semibold">
						{field.label}
					</span>
					<input
						className={inputClassName}
						autoFocus
						value={singleValue}
						onChange={(event) => setSingleValue(event.target.value)}
						placeholder={field.placeholder}
						type={field.type}
						inputMode={field.inputMode}
						disabled={state.isBusy}
					/>
				</label>
				<button
					className={primaryButtonClassName}
					type="submit"
					disabled={state.isBusy || !singleValue.trim()}
				>
					{state.isBusy ? "Submitting..." : field.buttonLabel}
				</button>
				{inputStep === "auth-code" ? (
					<div className="flex items-center justify-between gap-3 px-1 py-1">
						<span className="tg-text-muted text-[0.86rem]">Wrong number?</span>
						<button
							className={secondaryButtonClassName}
							type="button"
							onClick={() => void handleLogout()}
							disabled={state.isBusy || restarting}
						>
							{restarting ? "Restarting..." : "Use different number"}
						</button>
					</div>
				) : null}
			</form>
		);
	};

	return (
		<section className="tg-surface rounded-2xl p-5">
			<div>
				<span className="tg-text-secondary mb-2 inline-flex text-[0.72rem] font-semibold uppercase tracking-[0.13em]">
					Telegram account
				</span>
				<h3 className="tg-text-primary text-[1.15rem] font-bold leading-tight">
					{state.title}
				</h3>
				<p className="tg-text-muted mt-1.5 text-[0.92rem] leading-7">
					{state.description}
				</p>
			</div>
			{renderBody()}
			{(state.error || error) && state.step !== "error" ? (
				<div
					className="tg-alert-error mt-4 rounded-2xl px-4 py-3"
					role="alert"
				>
					{error ?? state.error}
				</div>
			) : null}
		</section>
	);
};
